import FiberManualRecord from '@mui/icons-material/FiberManualRecord'
import {
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material'
import ConfirmDialog from 'components/Dialog/ConfirmDialog'
import { isEmpty, uniq } from 'lodash-es'
import { ItemUnitContract, ModifierOptionDataContract } from 'models'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

function EachItemRow({ name }: { name: string }) {
    return (
        <ListItem sx={{ p: 0 }}>
            <ListItemIcon>
                <FiberManualRecord sx={{ height: '16px' }} />
            </ListItemIcon>
            <ListItemText>
                <Typography variant="body2">{name}</Typography>
            </ListItemText>
        </ListItem>
    )
}

function RequestToRemoveIngredientDialog({
    open,
    elementOfItemUnits,
    modifierOptions,
    onClose,
    onRemoveAndDeactivateIngredient,
    itemType,
}: {
    open: boolean
    onClose: () => void
    onRemoveAndDeactivateIngredient: () => void
    elementOfItemUnits: ItemUnitContract[]
    modifierOptions: ModifierOptionDataContract[]
    itemType: 'item' | 'ingredient'
}) {
    const { t } = useTranslation()
    const usedAsIngredientItems = useMemo(
        () =>
            uniq((elementOfItemUnits ?? []).flatMap((iu) => iu.itemName ?? '')),
        [elementOfItemUnits],
    )
    const usedAsModifierOptions = useMemo(
        () => uniq(modifierOptions.flatMap((iu) => iu.modifierGroupName ?? '')),
        [modifierOptions],
    )

    return (
        <ConfirmDialog
            open={open}
            onSave={onRemoveAndDeactivateIngredient}
            onDiscard={onClose}
            title={t('products.ingredient.title.deactivate', {
                itemType:
                    itemType.charAt(0).toLocaleUpperCase() + itemType.slice(1),
            })}
            confirmText={t('common.label.confirm')}
        >
            <Box>
                <Typography>
                    {t('products.ingredient.message.remove_from', {
                        itemType: itemType,
                    })}
                </Typography>
                <Box sx={{ pt: 1 }}>
                    {!isEmpty(usedAsIngredientItems) && (
                        <Box sx={{ ml: 2 }}>
                            <Typography fontWeight={'bold'} variant="body2">
                                {t('products.ingredient.label.products')}
                            </Typography>
                            <List>
                                {usedAsIngredientItems.map((itemName, idx) => (
                                    <EachItemRow key={idx} name={itemName} />
                                ))}
                            </List>
                        </Box>
                    )}
                    {!isEmpty(usedAsModifierOptions) && (
                        <Box sx={{ ml: 2 }}>
                            <Typography fontWeight={'bold'} variant="body2">
                                {t('products.ingredient.label.modifier_groups')}
                            </Typography>
                            <List>
                                {usedAsModifierOptions.map(
                                    (modifierGroupName, idx) => (
                                        <EachItemRow
                                            key={idx}
                                            name={modifierGroupName}
                                        />
                                    ),
                                )}
                            </List>
                        </Box>
                    )}
                    <Typography>
                        {t(
                            'products.ingredient.message.confirm_to_deactivate',
                            { itemType: itemType },
                        )}
                    </Typography>
                </Box>
            </Box>
        </ConfirmDialog>
    )
}

export default RequestToRemoveIngredientDialog
